// import { yupResolver } from '@hookform/resolvers/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import type { Addresses } from 'api/types/addresses.types';
import { BackArrow } from 'assets/icons';
import Button from 'components/atoms/Button';
import Footer from 'components/molecules/Footer';
import { BUTTON_NAMES } from 'constant/button-names';
import Page from 'layout/page';
import { fetchAdressLabels } from 'packages/shared/newOrder/constants';
import { StyledFormLabel, StyledInput } from 'pages/login/features/styles';
import React, { useEffect } from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { type AddressForm, addressSchema } from 'schemas/address-schema';
import styled from 'styled-components';

interface Props {
  onSubmit(address?: AddressForm): any;
  isLoading?: boolean;
  action?: 'add' | 'update';
}

const StyledFooter = styled(Footer)`
  box-shadow: unset;
`;

const StyledBackButton = styled.button`
  style: none;
  background: ${({ theme }) => theme.components.onboardingBottomSheet.button.backgroundColor};
  border: ${({ theme }) =>
    `${theme.components.onboardingBottomSheet.button.border.width}px solid ${theme.components.onboardingBottomSheet.button.border.color}`};
  border-radius: 40px;
  height: 48px;
  padding: 0 25px;
`;

const StyledButtonsRow = styled.div`
  display: flex;
  gap: 10px;
`;

interface LocationState extends Addresses.Geofence {
  lat: number;
  lng: number;
  placeId?: string;
  addressType: Addresses.AddressTypeT;
  address: Addresses.Address;
}

export const AddressFormPage: React.FC<Props> = ({ onSubmit, action, isLoading }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const geofence = location.state as LocationState;
  const { t } = useTranslation();

  const fields = fetchAdressLabels(geofence.addressType);

  const defaultValues = fields.reduce(
    (acc, field) => {
      // eslint-disable-next-line no-param-reassign
      acc[field.name] = '';
      return acc;
    },
    {} as Record<string, any>
  );

  const {
    control,
    register,
    errors,
    setValue,
    formState: { isValid },
    handleSubmit,
  } = useForm<AddressForm>({
    resolver: yupResolver(addressSchema),
    defaultValues: {
      ...defaultValues,
      ...geofence.address,
      addressType: geofence.addressType,
    },
    mode: 'onChange',
  });

  // register inputs that don't exist
  useEffect(() => {
    register({ name: 'geofenceId' });
    register({ name: 'geofenceLabel' });
    register({ name: 'cityId' });
    register({ name: 'addressType' });
    register({ name: 'lng' });
    register({ name: 'lat' });
    register({ name: 'placeId' });
  }, [register]);

  useEffect(() => {
    if (!geofence || !geofence.id || !geofence.cityId || !geofence.label || !geofence.lat || !geofence.lng) {
      navigate('/newOrder/addAddress', { replace: true });
      return;
    }
    // clear geofence Id
    setValue('geofenceId', geofence.id);
    setValue('geofenceLabel', geofence.label);
    setValue('cityId', geofence.cityId);
    setValue('placeId', geofence.placeId);

    // set coordinates
    setValue('lng', geofence.lng);
    setValue('lat', geofence.lat);
  }, [setValue, geofence, navigate]);

  const onAddressFormSubmit = async (addressForm: AddressForm) => {
    await onSubmit(addressForm);
  };

  const onGoBack = () => {
    navigate(-1);
  };

  const onErrorForm = () => {
    // handle Error
  };

  return (
    <Page title={action === 'update' ? t('common.editAddress') : t('common.newAddress')} hasBack route={-1}>
      <Form onSubmit={handleSubmit(onAddressFormSubmit, onErrorForm)}>
        <Form.Row>
          <Col xs={12}>
            {fields.map((field) => (
              <Controller
                key={field.name}
                name={field.name}
                control={control}
                defaultValue=""
                render={({ ...fieldProps }) => (
                  <div className="mt-2 w-100">
                    <StyledFormLabel>{field.label}</StyledFormLabel>
                    <StyledInput {...fieldProps} autoFocus={field.autoFocus} />
                    <Form.Control.Feedback type="invalid">{errors[field.name]?.message}</Form.Control.Feedback>
                  </div>
                )}
              />
            ))}
          </Col>
        </Form.Row>
        <StyledFooter showBackgroundColor={false}>
          <StyledButtonsRow>
            <StyledBackButton onClick={onGoBack}>
              <BackArrow />
            </StyledBackButton>
            <Button isLoading={isLoading} disabled={!isValid} type="submit" size="lg" block variant="primary" buttonName={BUTTON_NAMES.SAVE_ADDRESS}>
              {t('common.saveAddressAndConfirm')}
            </Button>
          </StyledButtonsRow>
        </StyledFooter>
      </Form>
    </Page>
  );
};
